// REACT IMPORTS
import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

// COMPONENT IMPORTS
import InputField from "components/form/InputField";
import Button from "components/form/Button";
import { useAppDispatch } from "store/hooks";
import {
  signUpDetailsGet,
  signUpUser,
  useAccountDetails,
} from "store/slices/authSlice";
import { InfoRedCircleIcon } from "icons/subscription/InfoRedCircleIcon";
import { decode, validateEmail } from "utils/HelperFunctions";
import { SettingsValue } from "types/payments";
import { GOOGLE_CLIENT_ID } from "store/api/constApi";
import GoogleLoginButton from "./GoogleLoginButton";
import { useGoogleAuth } from "hooks/useGoogleAuth";
import { useTid } from "utils/testid";

/* ------------------------------------- SIGN UP FORM ------------------------------------- */

const SignUpForm = () => {
  const tid = useTid();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const accountDetails = useAccountDetails();
  const { clusterId: cluster_id } = useParams();
  const clusterId = decode(cluster_id || "", true);
  const initialVal = {
    email: "",
  };

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [values, setValues] = useState<any>(initialVal);
  const [isErr, setIsErr] = useState<boolean>(false);
  const [isFormatErr, setIsFormatErr] = useState<boolean>(false);

  useEffect(() => {
    if (isErr) {
      setIsErr(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values]);

  const onSubmit = async (e: any) => {
    e.preventDefault();
    if (!validateEmail(values?.email || "")) {
      setIsFormatErr(true);
      return;
    }
    setIsFormatErr(false);
    setIsLoading(true);
    try {
      let payload = { ...values, clusterId: clusterId };
      let res: any = await dispatch(signUpDetailsGet(payload)).unwrap();
      if (res && res?.body?.success) {
        if (res?.body?.data?.isEmailMutiple === "one") {
          if (res?.body?.data?.getAccounts?.[0]?.isPasswordSet === "true") {
            navigate(`/${cluster_id}/sign-up-password`, {
              state: { email: values?.email },
            });
          } else {
            onSentOtp(res?.body?.data);
          }
        } else if (res?.body?.data?.isEmailMutiple === "multiple") {
          navigate(`/${cluster_id}/sign-up-email-attached`, {
            state: {
              data: res?.body?.data || [],
              email: values?.email,
            },
          });
          setIsLoading(false);
        } else {
          setIsErr(true);
          setIsLoading(false);
        }
      }
    } catch (error: any) {
      setIsLoading(false);
      console.log("sign up err: -->", error?.message);
    }
  };

  const onSentOtp = async (data: any) => {
    setIsLoading(true);
    try {
      let payload: any = {
        email: values?.email,
        isEmailMutiple: "one",
        // accounts_id must be the customer-facing account number (accountid),
        // not the internal record id — /portal/signup matches on accountid
        accounts_id: data?.getAccounts?.[0]?.accountid,
        clusterId: clusterId,
      };
      let res: any = await dispatch(signUpUser(payload)).unwrap();
      if (res?.body?.success) {
        navigate(`/${cluster_id}/sign-up-otp-verification`, {
          state: {
            data: data || [],
            email: values?.email,
            accounts_id: data?.getAccounts?.[0]?.accountid,
          },
        });
      }
      setIsLoading(false);
    } catch (error: any) {
      setIsLoading(false);
      console.log("send verification code error: -->", error?.message);
    }
  };

  const checkIsDisabled = () => {
    return Object.values(values).every((value) => value === "");
  };

  // A Google-verified email proves ownership, so "Continue with Google"
  // logs the user straight in when their email matches an account — no
  // OTP or password setup needed. No match shows the same "Email not
  // recognized" message as the regular sign-up.
  const { onGoogleCredential } = useGoogleAuth({
    onStart: () => setIsLoading(true),
    onFinish: () => setIsLoading(false),
    onNoMatch: () => setIsErr(true),
  });

  return (
    <div
      className="animate__animated animate__fadeIn w-full lg:max-w-[520px]"
      data-testid={tid("form-shell")}
    >
      <div className="lg:text-3xl text-xl font-bold -tracking-[0.6px] leading-normal text-txtPrimary">
        Welcome - Secure Payment Portal
      </div>
      <p className="lg:py-10 pt-6 pb-10 max-w-[520px] text-txtParagraph lg:text-base text-sm leading-normal font-normal">
        Please enter your email to create a new account.
      </p>
      <form onSubmit={onSubmit} noValidate data-testid={tid("sign-up-form")}>
        <div className="max-w-[520px] flex flex-col lg:gap-[60px] gap-10">
          <div className="flex flex-col gap-3">
            <InputField
              type="email"
              name="email"
              label="Email"
              data-testid={tid("email-input")}
              aria-label="Email address"
              placeholder="Enter your email address"
              value={values?.email}
              onChange={(e: any) => {
                if (isFormatErr) {
                  setIsFormatErr(false);
                }
                setValues({
                  ...values,
                  email: e?.target?.value,
                });
              }}
              _className="!font-medium"
            />
            {isFormatErr && (
              <div
                className="flex items-center gap-[5px] px-[10px] py-3 bg-lightRedBg rounded select-none animate__animated animate__fadeIn"
                role="alert"
                data-testid={tid("email-format-alert")}
              >
                <InfoRedCircleIcon aria-hidden="true" />
                <div className="lg:text-base text-sm text-red leading-[20.83px]">
                  Please enter a valid email address.
                </div>
              </div>
            )}
          </div>
          <div className="w-full">
            <Button
              className="w-full"
              text="Next"
              type="submit"
              data-testid={tid("next-button")}
              aria-label="Continue sign up"
              isDisabled={checkIsDisabled()}
              isLoading={isLoading}
              variant="variable"
            />
            {isErr && (
              <>
                <div
                  className="flex items-center gap-[5px] mt-3 px-[10px] py-3 bg-lightRedBg rounded select-none animate__animated animate__fadeIn"
                  role="alert"
                  data-testid={tid("email-not-recognized-alert")}
                >
                  <InfoRedCircleIcon aria-hidden="true" />
                  <div className="lg:text-base text-sm text-red leading-[20.83px]">
                    Email not recognized.
                  </div>
                </div>
                {accountDetails?.data &&
                  accountDetails?.data.request_access === SettingsValue.YES && (
                    <div className="mt-4 text-iconBright lg:text-base text-sm leading-[20.83px] animate__animated animate__fadeIn">
                      <p>
                        It looks like this email isn't associated with an
                        account yet. If you're new here,{" "}
                        <Link
                          to={`/${cluster_id}/request-access`}
                          className="text-blue hover:!text-btnPrimaryHover"
                          data-testid={tid("request-access-link")}
                          aria-label="Request access"
                        >
                          request access
                        </Link>{" "}
                        to join us!
                      </p>
                    </div>
                  )}
                {/* <div className="pt-4 text-base text-blue hover:!text-btnPrimaryHover leading-normal font-medium flex justify-center">
                  <div className="cursor-pointer" onClick={() => navigate(-1)}>
                    Go back
                  </div>
                </div> */}
              </>
            )}
            {GOOGLE_CLIENT_ID ? (
              <div className="flex flex-col gap-3 mt-4">
                <div className="flex items-center gap-[6px]">
                  <div className="h-[1px] w-full bg-strokePrimary" />
                  <div className="flex whitespace-nowrap text-sm text-txtSecondary font-normal leading-normal">
                    or
                  </div>
                  <div className="h-[1px] w-full bg-strokePrimary" />
                </div>
                <GoogleLoginButton
                  onCredential={onGoogleCredential}
                  text="continue_with"
                  data-testid={tid("google-login-button")}
                />
              </div>
            ) : null}
          </div>
        </div>
      </form>
      {!isErr && (
        <div className="max-w-[520px] flex flex-col gap-4 items-center pt-4">
          <div className="flex gap-[6px] items-center justify-start w-full">
            <p className="lg:text-base text-sm text-txtParagraph leading-normal">
              Already have an account?
            </p>
            <Link
              to={`/${cluster_id}/login`}
              className="cursor-pointer lg:text-base text-sm text-blue hover:!text-btnPrimaryHover font-medium leading-normal"
              data-testid={tid("login-link")}
              aria-label="Log in instead"
            >
              Login
            </Link>
          </div>
        </div>
      )}
    </div>
  );
};

export default SignUpForm;
